/**
 * Onboarding State Machine
 * 
 * Manages the state-driven onboarding flow for Catalog and Bookings apps.
 * Tracks user progress, checks existing data, and generates dynamic button states.
 */

import { getDataAPI } from './fetchData';

// Onboarding phases
export const ONBOARDING_PHASES = {
  NOT_STARTED: 'not_started',
  CATALOG_INTRO: 'catalog_intro',
  CATALOG_EXPLORING: 'catalog_exploring',
  CATALOG_IMPORTING: 'catalog_importing',
  CATALOG_ADD_RELEASE: 'catalog_add_release',
  CATALOG_COMPLETE: 'catalog_complete',
  BOOKINGS_INTRO: 'bookings_intro',
  BOOKINGS_EXPLORING: 'bookings_exploring',
  BOOKINGS_ADD_SHOW: 'bookings_add_show',
  BOOKINGS_COMPLETE: 'bookings_complete',
  WORKFLOWS_INTRO: 'workflows_intro',
  COMPLETE: 'complete'
};

// Orange button text for each phase
export const BUTTON_TEXT_MAP = {
  [ONBOARDING_PHASES.NOT_STARTED]: 'Start Your Tour',
  [ONBOARDING_PHASES.CATALOG_INTRO]: 'Show me Catalog',
  [ONBOARDING_PHASES.CATALOG_EXPLORING]: 'Click Here To Explore Catalog',
  [ONBOARDING_PHASES.CATALOG_IMPORTING]: 'Catalog is Loading...',
  [ONBOARDING_PHASES.CATALOG_ADD_RELEASE]: 'Add Your Upcoming Release',
  [ONBOARDING_PHASES.CATALOG_COMPLETE]: 'Go to Next Step',
  [ONBOARDING_PHASES.BOOKINGS_INTRO]: 'Show me Bookings',
  [ONBOARDING_PHASES.BOOKINGS_EXPLORING]: 'Explore Bookings',
  [ONBOARDING_PHASES.BOOKINGS_ADD_SHOW]: 'Let\'s add your first show',
  [ONBOARDING_PHASES.BOOKINGS_COMPLETE]: 'Bookings App is now set up, let\'s move to the next step',
  [ONBOARDING_PHASES.WORKFLOWS_INTRO]: 'Show the Workflows',
  [ONBOARDING_PHASES.COMPLETE]: 'Complete'
};

// Inside-app instruction text for each phase
export const INSTRUCTION_TEXT_MAP = {
  [ONBOARDING_PHASES.CATALOG_ADD_RELEASE]: 'Fill in the fields above, we will fill in the rest later',
  [ONBOARDING_PHASES.BOOKINGS_ADD_SHOW]: 'Let\'s add your first show, fill in the fields above'
};

// LocalStorage keys
const ONBOARDING_STATE_KEY = 'dizee_onboarding_state';
const ONBOARDING_DATA_KEY = 'dizee_onboarding_data';

/**
 * Get current onboarding state
 */
export const getOnboardingState = () => {
  try {
    const saved = localStorage.getItem(ONBOARDING_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading onboarding state:', error);
  }
  
  // Default state
  return {
    currentPhase: ONBOARDING_PHASES.NOT_STARTED,
    catalogHasData: false,
    bookingsHasData: false,
    releaseAdded: false,
    showAdded: false,
    catalogImported: false,
    isActive: false,
    startedAt: null,
    completedAt: null
  };
};

/**
 * Save onboarding state
 */
export const saveOnboardingState = (state) => {
  try {
    localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving onboarding state:', error);
  }
};

/**
 * Update onboarding state
 */
export const updateOnboardingState = (updates) => {
  const currentState = getOnboardingState();
  const newState = { ...currentState, ...updates };
  saveOnboardingState(newState);
  return newState;
};

/**
 * Start onboarding
 */
export const startOnboarding = () => {
  return updateOnboardingState({
    isActive: true,
    startedAt: Date.now(),
    currentPhase: ONBOARDING_PHASES.CATALOG_INTRO
  });
};

/**
 * Complete onboarding
 */
export const completeOnboarding = () => {
  return updateOnboardingState({
    isActive: false,
    currentPhase: ONBOARDING_PHASES.COMPLETE,
    completedAt: Date.now()
  });
};

/**
 * Check if onboarding is active
 */
export const isOnboardingActive = () => {
  const state = getOnboardingState();
  return state.isActive && state.currentPhase !== ONBOARDING_PHASES.COMPLETE;
};

/**
 * Transition to next phase
 */
export const transitionToPhase = (phase) => {
  return updateOnboardingState({ currentPhase: phase });
};

/**
 * Get current phase
 */
export const getCurrentPhase = () => {
  const state = getOnboardingState();
  return state.currentPhase;
};

/**
 * Get orange button text for current phase
 */
export const getButtonText = () => {
  const phase = getCurrentPhase();
  return BUTTON_TEXT_MAP[phase] || 'Continue';
};

/**
 * Get instruction text for current phase (used inside apps)
 */
export const getInstructionText = () => {
  const phase = getCurrentPhase();
  return INSTRUCTION_TEXT_MAP[phase] || '';
};

/**
 * Check user's existing data and update state
 */
export const checkUserData = async () => {
  try {
    const [catalogRes, bookingsRes] = await Promise.all([
      getDataAPI('user/search-catalog').catch(() => null),
      getDataAPI('user/getAllBookings').catch(() => null)
    ]);
    
    const catalogCount = catalogRes?.data?.items?.length || catalogRes?.data?.length || 0;
    const bookingsCount = bookingsRes?.data?.bookings?.length || 0;
    
    updateOnboardingState({
      catalogHasData: catalogCount > 0,
      bookingsHasData: bookingsCount > 0
    });
    
    return {
      catalogCount,
      bookingsCount,
      catalogHasData: catalogCount > 0,
      bookingsHasData: bookingsCount > 0
    };
  } catch (error) {
    console.error('Error checking user data:', error);
    return {
      catalogCount: 0,
      bookingsCount: 0,
      catalogHasData: false,
      bookingsHasData: false
    };
  }
};

/**
 * Mark catalog as imported
 */
export const markCatalogImported = () => {
  return updateOnboardingState({ 
    catalogImported: true,
    catalogHasData: true
  });
};

/**
 * Mark release as added
 */
export const markReleaseAdded = () => {
  return updateOnboardingState({ 
    releaseAdded: true,
    currentPhase: ONBOARDING_PHASES.CATALOG_COMPLETE
  });
};

/**
 * Mark show as added
 */
export const markShowAdded = () => {
  return updateOnboardingState({ 
    showAdded: true,
    currentPhase: ONBOARDING_PHASES.BOOKINGS_COMPLETE
  });
};

/**
 * Reset onboarding (for testing)
 */
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_STATE_KEY);
  localStorage.removeItem(ONBOARDING_DATA_KEY);
  return getOnboardingState();
};

/**
 * Should show onboarding?
 */
export const shouldShowOnboarding = () => {
  // Check if user has completed tutorial
  const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
  const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
  
  if (!hasSeenTutorial || !hasSeenWalkthrough) {
    return false;
  }
  
  // Check if already completed
  const state = getOnboardingState();
  return state.currentPhase !== ONBOARDING_PHASES.COMPLETE && !state.completedAt;
};

/**
 * Get appropriate phase based on current location and data
 */
export const getPhaseForLocation = async (pathname) => {
  const state = getOnboardingState();
  const userData = await checkUserData();
  
  // If at catalog and exploring
  if (pathname.includes('/catalog')) {
    if (pathname.includes('/add')) {
      return ONBOARDING_PHASES.CATALOG_ADD_RELEASE;
    }
    if (state.catalogImported || userData.catalogHasData) {
      return ONBOARDING_PHASES.CATALOG_COMPLETE;
    }
    return ONBOARDING_PHASES.CATALOG_EXPLORING;
  }
  
  // If at bookings and exploring
  if (pathname.includes('/bookings')) {
    if (pathname.includes('/add')) {
      return ONBOARDING_PHASES.BOOKINGS_ADD_SHOW;
    }
    if (state.showAdded || userData.bookingsHasData) {
      return ONBOARDING_PHASES.BOOKINGS_COMPLETE;
    }
    return ONBOARDING_PHASES.BOOKINGS_EXPLORING;
  }
  
  // If at workflows
  if (pathname.includes('/flows') || pathname.includes('/reminders')) {
    return ONBOARDING_PHASES.WORKFLOWS_INTRO;
  }
  
  return state.currentPhase;
};


